/* eslint-disable no-console */
import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import {
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Card,
    CardContent,
    CardActions,
    CircularProgress,
    Snackbar
} from "@mui/material";
import { Add, Edit, Delete, Close, DragIndicator } from "@mui/icons-material";
import { firestore, storage } from "../services/firebase";
import { THEME_MAP } from "../theme";
import { captureError } from "../lib/errorReporting";

export function HowToEditor() {
    const [howToItems, setHowToItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    
    // Form state
    const [formData, setFormData] = useState({
        title: "",
        description: "",
        imageUrl: "",
        order: 0
    });

    // Snackbar state
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success"
    });

    const fileInputRef = React.useRef();

    const showSnackbar = useCallback((message, severity = "success") => {
        setSnackbar({ open: true, message, severity });
    }, []);

    const fetchHowToItems = useCallback(async () => {
        setIsLoading(true);
        try {
            const snapshot = await firestore
                .collection("HowTo")
                .orderBy("order", "asc")
                .get();
            
            const items = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            
            setHowToItems(items);
        } catch (error) {
            captureError(error, { view: "HowToEditor", action: "fetchHowToItems" });
            console.error("Error fetching How To items:", error);
            showSnackbar("Error fetching How To items", "error");
        } finally {
            setIsLoading(false);
        }
    }, [showSnackbar]);

    useEffect(() => {
        fetchHowToItems();
    }, [fetchHowToItems]);

    const handleOpenDialog = (item = null) => {
        if (item) {
            setEditingItem(item);
            setFormData({
                title: item.title || "",
                description: item.description || "",
                imageUrl: item.imageUrl || "",
                order: item.order || 0
            });
        } else {
            setEditingItem(null);
            setFormData({
                title: "",
                description: "",
                imageUrl: "",
                order: howToItems.length
            });
        }
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setEditingItem(null);
        setFormData({ title: "", description: "", imageUrl: "", order: 0 });
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setIsUploading(true);
        try {
            const timestamp = Date.now();
            const imageRef = storage.ref(`howto-images/${timestamp}_${file.name}`);
            await imageRef.put(file);
            const downloadURL = await imageRef.getDownloadURL();
            
            setFormData(prev => ({
                ...prev,
                imageUrl: downloadURL
            }));
            
            showSnackbar("Image uploaded successfully");
        } catch (error) {
            captureError(error, { view: "HowToEditor", action: "uploadImage" });
            console.error("Error uploading image:", error);
            showSnackbar("Error uploading image", "error");
        } finally {
            setIsUploading(false);
        }
    };

    const handleSave = async () => {
        try {
            const data = {
                title: formData.title.trim(),
                description: formData.description.trim(),
                imageUrl: formData.imageUrl,
                order: parseInt(formData.order),
                updatedAt: new Date()
            };

            if (editingItem) {
                await firestore.collection("HowTo").doc(editingItem.id).update(data);
                showSnackbar("How To item updated successfully");
            } else {
                data.createdAt = new Date();
                await firestore.collection("HowTo").add(data);
                showSnackbar("How To item created successfully");
            }

            handleCloseDialog();
            fetchHowToItems();
        } catch (error) {
            captureError(error, { view: "HowToEditor", action: "saveHowToItem" });
            console.error("Error saving How To item:", error);
            showSnackbar("Error saving How To item", "error");
        }
    };

    const handleDelete = async (itemId) => {
        if (!window.confirm("Are you sure you want to delete this How To item?")) {
            return;
        }

        try {
            await firestore.collection("HowTo").doc(itemId).delete();
            showSnackbar("How To item deleted successfully");
            fetchHowToItems();
        } catch (error) {
            captureError(error, { view: "HowToEditor", action: "deleteHowToItem" });
            console.error("Error deleting How To item:", error);
            showSnackbar("Error deleting How To item", "error");
        }
    };

    const handleReorder = async (itemId, newOrder) => {
        try {
            await firestore.collection("HowTo").doc(itemId).update({ order: newOrder });
            fetchHowToItems();
        } catch (error) {
            captureError(error, { view: "HowToEditor", action: "reorderHowToItem" });
            console.error("Error reordering item:", error);
            showSnackbar("Error reordering item", "error");
        }
    };

    if (isLoading) {
        return (
            <Container>
                <LoadingContainer>
                    <CircularProgress />
                </LoadingContainer>
            </Container>
        );
    }

    return (
        <Container>
            <HeaderContainer>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    onClick={() => handleOpenDialog()}
                >
                    Add New Step
                </Button>
            </HeaderContainer>

            <ItemsContainer>
                {howToItems.length === 0 ? (
                    <NoItemsContainer>
                        <h2>No How-To steps created yet</h2>
                        <p>Click &quot;Add New Step&quot; to get started!</p>
                    </NoItemsContainer>
                ) : (
                    howToItems.map((item, index) => (
                        <ItemCard key={item.id}>
                            <CardContent>
                                <ItemHeader>
                                    <div>
                                        <DragIndicator style={{ color: THEME_MAP["color-basic-500"] }} />
                                        <span>Step {index + 1}</span>
                                    </div>
                                    <OrderControls>
                                        <Button
                                            size="small"
                                            onClick={() => handleReorder(item.id, Math.max(0, item.order - 1))}
                                            disabled={index === 0}
                                        >
                                            ↑
                                        </Button>
                                        <Button
                                            size="small"
                                            onClick={() => handleReorder(item.id, item.order + 1)}
                                            disabled={index === howToItems.length - 1}
                                        >
                                            ↓
                                        </Button>
                                    </OrderControls>
                                </ItemHeader>
                                
                                <ItemContent>
                                    <ImagePreview>
                                        {item.imageUrl ? (
                                            <img src={item.imageUrl} alt={item.title} />
                                        ) : (
                                            <div>No image</div>
                                        )}
                                    </ImagePreview>
                                    
                                    <TextContent>
                                        <h3>{item.title || "Untitled"}</h3>
                                        <p>{item.description || "No description"}</p>
                                    </TextContent>
                                </ItemContent>
                            </CardContent>
                            
                            <CardActions>
                                <Button
                                    startIcon={<Edit />}
                                    onClick={() => handleOpenDialog(item)}
                                >
                                    Edit
                                </Button>
                                <Button
                                    startIcon={<Delete />}
                                    color="error"
                                    onClick={() => handleDelete(item.id)}
                                >
                                    Delete
                                </Button>
                            </CardActions>
                        </ItemCard>
                    ))
                )}
            </ItemsContainer>

            {/* Edit/Add Dialog */}
            <Dialog open={isDialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingItem ? "Edit How To Step" : "Add New How To Step"}
                </DialogTitle>
                <DialogContent>
                    <FormContainer>
                        <TextField
                            label="Step Title"
                            value={formData.title}
                            onChange={(e) => handleInputChange("title", e.target.value)}
                            fullWidth
                            margin="normal"
                        />
                        
                        <TextField
                            label="Description"
                            value={formData.description}
                            onChange={(e) => handleInputChange("description", e.target.value)}
                            fullWidth
                            multiline
                            rows={4}
                            margin="normal"
                        />
                        
                        <TextField
                            label="Order"
                            type="number"
                            value={formData.order}
                            onChange={(e) => handleInputChange("order", e.target.value)}
                            margin="normal"
                        />
                        
                        <ImageUploadSection>
                            <Button
                                variant="outlined"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={isUploading}
                            >
                                {isUploading ? <CircularProgress size={20} /> : "Upload Image"}
                            </Button>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleImageUpload}
                                accept="image/*"
                                style={{ display: "none" }}
                            />
                            
                            {formData.imageUrl && (
                                <ImagePreview>
                                    <img src={formData.imageUrl} alt="Preview" />
                                </ImagePreview>
                            )}
                        </ImageUploadSection>
                    </FormContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button 
                        onClick={handleSave}
                        variant="contained" 
                        color="primary"
                        disabled={!formData.title.trim()}
                    >
                        {editingItem ? "Update" : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                message={snackbar.message}
                action={
                    <IconButton
                        size="small"
                        color="inherit"
                        onClick={() => setSnackbar({ ...snackbar, open: false })}
                    >
                        <Close fontSize="small" />
                    </IconButton>
                }
            />
        </Container>
    );
}

const Container = styled.div`
    height: 100%;
    width: 100%;
    padding: 24px;
    overflow: auto;
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 300px;
`;

const HeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    
    h1 {
        color: ${THEME_MAP["color-primary-500"]};
        margin: 0;
    }
`;

const ItemsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const NoItemsContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 300px;
    text-align: center;
    color: ${THEME_MAP["color-basic-600"]};
`;

const ItemCard = styled(Card)`
    .MuiCardContent-root {
        padding: 16px;
    }
`;

const ItemHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    
    > div:first-child {
        display: flex;
        align-items: center;
        gap: 8px;
        font-weight: 500;
        color: ${THEME_MAP["color-primary-500"]};
    }
`;

const OrderControls = styled.div`
    display: flex;
    gap: 4px;
`;

const ItemContent = styled.div`
    display: flex;
    gap: 16px;
`;

const ImagePreview = styled.div`
    width: 120px;
    height: 80px;
    border: 1px solid ${THEME_MAP["color-basic-300"]};
    border-radius: 4px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${THEME_MAP["color-basic-200"]};
    flex-shrink: 0;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    div {
        color: ${THEME_MAP["color-basic-600"]};
        font-size: 12px;
    }
`;

const TextContent = styled.div`
    flex: 1;
    
    h3 {
        margin: 0 0 8px 0;
        color: ${THEME_MAP["color-primary-500"]};
    }
    
    p {
        margin: 0;
        color: ${THEME_MAP["color-basic-700"]};
        line-height: 1.4;
    }
`;

const FormContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-top: 8px;
`;

const ImageUploadSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 16px;
`;